import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Chip, IconButton, Menu, MenuItem, Avatar, Divider,
} from '@mui/material';
import { 
  Home, Chat, People, Inventory, MenuBook, Logout, AccountCircle, 
  Business, Settings, WhatsApp, Assessment, EditNote, Assignment
} from '@mui/icons-material';
import { logout, selectUser, selectIsSuperAdmin } from '../../store/authSlice';
import { canAccessPermission } from '../../config/permissions';
import config from '../../config/config';

const API_BASE_URL = config.API_BASE_URL + '/';
const drawerWidth = 260;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const allNavItems = [
    { path: '/dashboard', icon: <Home />, label: 'Dashboard', permission: 'dashboard.view' },
    { path: '/conversations', icon: <Chat />, label: 'Conversations', permission: 'conversations.view' },
    { path: '/instructions', icon: <MenuBook />, label: 'Instructions', permission: 'instructions.view' },
    { path: '/staff', icon: <People />, label: 'Staff', permission: 'staff.view' },
    { path: '/items', icon: <Inventory />, label: 'Products', permission: 'items.view' },
    { path: '/users', icon: <People />, label: isSuperAdmin ? 'All Users' : 'Manage Users', permission: 'users.view' },
    { path: '/whatsapp-numbers', icon: <WhatsApp />, label: 'WhatsApp Numbers', permission: 'whatsapp_numbers.view' },
    { path: '/whatsapp-templates', icon: <Assignment />, label: 'WA Templates', permission: 'whatsapp_templates.view' },
    { path: '/customer-summary', icon: <Assessment />, label: 'Customer Summary', permission: 'customer_summaries.view' },
    { path: '/summary-instructions', icon: <EditNote />, label: 'Summary Instructions', permission: 'summary_instructions.view' },
    { path: '/companies', icon: <Business />, label: 'Companies', permission: 'companies.view' },
  ];

  const navItems = allNavItems.filter((item) => canAccessPermission(user, item.permission));

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Avatar
              src={user?.company_logo ? `${API_BASE_URL}${user.company_logo}` : undefined}
              alt={user?.company_name}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                '& img': {
                  objectFit: 'cover'
                }
              }}
            >
              <Business />
            </Avatar>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {user?.company_name || 'W.H.D. an IT Solution Provider'}
            </Typography>
          </Box>
          <Chip
            label={user?.role === 'super_admin' ? 'Super Admin' : 'Active'}
            color={user?.role === 'super_admin' ? 'secondary' : 'success'}
            size="small"
            sx={{ fontWeight: 500, mr: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {user?.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'company_admin' ? 'Admin' : 'User'}
              </Typography>
            </Box>
            <IconButton
              size="large"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar
                sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                src={user?.profile_image ? `${API_BASE_URL}${user.profile_image}` : undefined}
              >
                {user?.full_name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: '#1e293b',
            color: 'white',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{
          p: 3,
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1
        }}>
          <Box
            component="img"
            src="/assets/logo.png"
            alt="World Home Development"
            sx={{
              width: 60,
              height: 60,
              objectFit: 'contain',
              mb: 1
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            World Home Development
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Admin Panel
          </Typography>
        </Box>

        <List sx={{ px: 2, py: 2 }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: isActive ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? '#60a5fa' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      color: isActive ? 'white' : 'rgba(255,255,255,0.8)',
                      '& .MuiTypography-root': { fontWeight: isActive ? 600 : 400 },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 'auto', p: 2 }}>
          <ListItemButton
            component="a"
            href="/chatbot"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <Chat />
            </ListItemIcon>
            <ListItemText
              primary="Live Chatbot"
              sx={{ color: 'rgba(255,255,255,0.8)' }}
            />
          </ListItemButton>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#f8fafc',
          minHeight: '100vh',
          p: 3,
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
